import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { useMyContext } from '../../context/Context';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import { ReactComponent as DcCheckbox } from "../../assets/checkbox_icons/Checkbox_dc.svg";
import { ReactComponent as DcCheckboxChecked } from "../../assets/checkbox_icons/Checkbox_dc_checked.svg";
import { downloadInventoryData, downloadWpidInventoryData } from '../../services/downloadService';
import FormGroup from '@mui/material/FormGroup';
import impstyles from '../../styles/DownloadComponentStyles.module.scss';
import SelectDcErrorMessage from './SelectDcErrorMessage';
import moment from 'moment';

const MuiDownloadDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    maxWidth: '900px',
    padding: '28px 36px',
    borderRadius: '8px'
  }
});

const DialogTitle = styled(MuiDialogTitle)({
  padding: '0px',
  marginBottom: '5px'
});

const DialogContent = styled(MuiDialogContent)({
  padding: '0px',
  overflow: 'hidden'
});

const DialogActions = styled(MuiDialogActions)({
  margin: 0,
  marginTop: '10px',
  padding: 0
});

const BlueCheckbox = styled(Checkbox)({
  color: '#0067A0',
  '&.Mui-checked': {
    color: '#0071E9'
  }
});

const CustomButton = styled(Button)({
  width: '168px',
  height: '48px',
  background: '#0071E9',
  borderRadius: '4px',
  marginLeft: '15px',
  fontFamily: 'canada-type-gibson, sans-serif',
  fontStyle: 'normal',
  fontWeight: '500',
  fontSize: '16px',
  textTransform: 'capitalize'
});

const CustomOutlineButton = styled(Button)({
  width: '168px',
  height: '48px',
  color: '#0071E9',
  border: 'solid 2px #0071E9',
  borderRadius: '4px',
  fontFamily: 'canada-type-gibson, sans-serif',
  fontStyle: 'normal',
  fontWeight: '500',
  fontSize: '16px',
  textTransform: 'capitalize'
});

export default function DownloadDialog(props) {
  const _ = require('lodash');
  const allDCsId = "All DC's";

  const context = useMyContext();
  const isDark = context.colorTheme === 'dark';

  let dcObj = context.dcNbrList.map(dcNum => {
    return { id: dcNum, checked: true };
  });
  dcObj.unshift({ id: allDCsId, checked: true });

  const [selectedDCs, setSelectedDCs] = useState([]);
  const [showError, setShowError] = useState(false);
  const [dcObjects, setDcObjects] = useState(dcObj);
  const [dateString, setDateString] = useState("");
  const selectDcErrorMessage = 'Please select a DC';

  useEffect(() => {
    if (context.searchedDcNbrList) {
      let dcValues = dcObjects;
      context.searchedDcNbrList.map(dc => {
        if ((dc + '').length !== 4) {
          dc = (dc + '').substring(1);
        }
        const selectedDCIndex = dcObjects.findIndex(
          data => data.id === parseInt(dc)
        );
        /* istanbul ignore next */
        if (selectedDCIndex !== -1) {
          dcValues[selectedDCIndex].checked = true;
        }
      });
      setDcObjects(dcValues);
      updateSelectedDCs();
    }
    setDateString(moment().format("MMM Do, YYYY"));
  }, [context.searchedDcNbrList]);

  const handleClose = () => {
    setDefaults();
    props.handleClose();
  };

  const setDefaults = () => {
    let selectedDCs = [];
    dcObjects.forEach(dcObj => {
      if (dcObj.id !== allDCsId) {
        selectedDCs.push(dcObj.id);
      }
    });
    setSelectedDCs(selectedDCs);
    setDcObjects(dcObj);
    setShowError(false);
  };

  const handleDownload = async () => {
    if (selectedDCs.length === 0) {
      setShowError(true);
      return;
    }
    props.handleDownloading(true);
    try {
      // Prefer the explicit isWpidSearch flag set at search time; fall back
      // to the data-shape check only when the flag isn't present (e.g.
      // existing test mocks built manually).
      const isNonVariantMode = context.isWpidSearch ?? !!context.selectedItem?.wpidNbr;
      if (isNonVariantMode) {
        await downloadWpidInventoryData(context.selectedItem, selectedDCs);
      } else {
        await downloadInventoryData(
          context.searchResult.map(item => item.legacyItemNbr),
          selectedDCs,
          props.exportType
        );
      }
    } catch (err) {
      console.error('Download failed', err);
      context.showMsgCallback('Download Failed');
    } finally {
      setDefaults();
      props.handleDownloading(false);
    }
  };

  const clubHandleChange = event => {
    if (event.target.value === allDCsId) {
      for (const dc of dcObjects) {
        dc.checked = event.target.checked;
      }
    } else {
      _.set(
        _.find(dcObjects, { id: parseInt(event.target.value) }),
        'checked',
        event.target.checked
      );
    }
    updateSelectedDCs();
  };

  const updateSelectedDCs = () => {
    let _selectedDCs = [];
    dcObjects.forEach(dcObj => {
      if (dcObj.checked && dcObj.id !== allDCsId) {
        _selectedDCs.push(dcObj.id);
      }
    });
    /* istanbul ignore else */
    if (_selectedDCs.length === Object?.values(dcObjects).length - 1) {
      dcObjects[0].checked = true;
    } else {
      dcObjects[0].checked = false;
    }
    setSelectedDCs(_selectedDCs);
    setShowError(false);
  };

  return (
    <MuiDownloadDialog
      aria-labelledby="customized-dialog-title"
      open={props.open}
      maxWidth={'md'}
      PaperProps={{
        style: isDark ? {
          backgroundColor: '#1A1A1A',
          color: '#E6E6E6'
        } : {}
      }}
    >
      <DialogTitle id="customized-dialog-title">
        {showError ? (
          <SelectDcErrorMessage errorMessage={selectDcErrorMessage} />
        ) : null}
        <span className={impstyles.dialogTitle}>
          Download set - select DC's
        </span>
        <br />
        <span className={impstyles.dialogSubtitle}>
          Retrieving data for {dateString} &#40;one week prior + two weeks into
          the future&#41;
        </span>
      </DialogTitle>
      <DialogContent>
        <div className={impstyles.DcListBorder}>
          <FormControl>
            <FormGroup style={{ flexDirection: 'row' }}>
              {dcObjects.map(cl => {
                return (
                  <div
                    className={impstyles.DcFormControlLabelBorder}
                    key={cl.id}
                  >
                    <FormControlLabel
                      icon={<span className={{ color: '#0060C6' }} />}
                      className={impstyles.downloadFormControlLabel}
                      key={cl.id}
                      control={
                        <BlueCheckbox
                          color="primary"
                          checked={cl.checked}
                          onChange={clubHandleChange}
                          value={cl.id}
                          control={<Checkbox icon={<DcCheckbox />} checkedIcon={<DcCheckboxChecked />} name="DcCheck" />}
                        />
                      }
                      label={
                        <Typography className={impstyles.formControlLabelText}>
                          {cl.id}
                        </Typography>
                      }
                    />
                  </div>
                );
              })}
            </FormGroup>
          </FormControl>
        </div>
      </DialogContent>
      <DialogActions className={impstyles.dialogActions}>
        <CustomOutlineButton
          id="CancelButton"
          variant="outlined"
          onClick={handleClose}
          color="primary"
        >
          Cancel
        </CustomOutlineButton>
        <div className={impstyles.buttonSpacer} />
        <CustomButton
          id="DownloadButton"
          variant="contained"
          color="primary"
          onClick={handleDownload}
        >
          Download
        </CustomButton>
      </DialogActions>
    </MuiDownloadDialog>
  );
}

DownloadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  exportType: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  handleDownloading: PropTypes.func.isRequired
};
